import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, Paper } from '@mui/material';
import { Close as CloseIcon, DragIndicator as DragIcon } from '@mui/icons-material';

interface FeaturePopupProps {
  feature: {
    name: string;
    properties: Record<string, any>;
    layerType: string;
  };
  position: { x: number; y: number };
  onClose: () => void;
  onZoom?: () => void;
}

// Configuration for different layer types and their property mappings
const LAYER_CONFIGS: Record<string, {
  title: string;
  fields: Array<{
    key: string;
    label: string;
    formatter?: (value: any) => string;
  }>;
  customHeader?: (properties: any) => string;
}> = {
  // Configuration for couloirs layer - must match exactly keys in couloirs.geojson
  // Based on current GeoJSON structure with French property names
  couloirs: {
    title: 'Couloir',
    fields: [
      { key: 'Superficie', label: 'Superficie' },
      { key: "Type d'activité", label: "Type d'activité" },
      { key: 'Wilaya', label: 'Wilaya' },
      { key: 'Moughataa', label: 'Moughataa' },
      { key: "Date d'attribution", label: "Date d'attribution" },
      { key: 'Numéro arrêté', label: 'Numéro arrêté' }
    ]
  },
  'permis-industriels': {
    title: 'Permis Industriel',
    fields: [
      { key: 'type_permis', label: 'Type de permis' },
      { key: 'superficie', label: 'Superficie', formatter: (val) => `${val} km²` },
      { key: 'wilaya', label: 'Wilaya' },
      { key: 'date_octroi', label: 'Date d\'octroi' },
      { key: 'numero_permis', label: 'Numéro permis' },
      { key: 'titulaire', label: 'Titulaire' }
    ]
  },
  forages: {
    title: 'Forage',
    fields: [
      { key: 'Nom', label: 'Nom' },
      { key: 'Coordonnées', label: 'Coordonnées' },
      { key: 'Date', label: 'Date' },
      { key: 'Wilaya', label: 'Wilaya' },
      { key: 'Profondeur', label: 'Profondeur' },
      { key: 'Débit', label: 'Débit' },
      { key: 'Équipé', label: 'Équipé' },
      { key: 'Exploité par', label: 'Exploité par' }
    ],
    customHeader: (properties: any) => properties.Nom ? `Forage ${properties.Nom}` : 'Forage'
  },
  'petite-mine': {
    title: 'PPEM',
    fields: [
      { key: 'Détenteur', label: 'Détenteur' },
      { key: 'Code', label: 'Code' },
      { key: "Date d'octroi", label: "Date d'octroi" },
      { key: "Date d'expiration", label: "Date d'expiration" },
      { key: 'Wilaya', label: 'Wilaya' },
      { key: 'Couloir', label: 'Couloir' },
      { key: 'Status', label: 'Status' },
      { key: 'Renouvellement', label: 'Renouvellement' },
      { key: 'Bornage', label: 'Bornage' },
      { key: 'Date NIE', label: 'Date NIE' },
      { key: 'Date demarrage des travaux', label: 'Date démarrage des travaux' },
      { key: 'Bulletin des travaux', label: 'Bulletin des travaux' },
      { key: 'Production', label: 'Production' }
    ],
    customHeader: (properties: any) => properties.Code ? `${properties.Code} D2` : 'PPEM'
  },
  mejhars: {
    title: 'Mejhar',
    fields: [
      { key: 'Nom', label: 'Nom' },
      { key: 'Zone', label: 'Zone' },
      { key: 'Statut', label: 'Statut' },
      { key: "Nombre d'exploitants", label: "Nombre d'exploitants" },
      { key: 'Total puits', label: 'Total puits' },
      { key: 'Puits actifs', label: 'Puits actifs' },
      { key: 'Puits non actifs', label: 'Puits non actifs' },
      { key: "Type d'exploitation", label: "Type d'exploitation" },
      { key: "Date debut d'exploitation", label: "Date début d'exploitation" },
      { key: 'Succursale', label: 'Succursale' },
      { key: 'Accidents enregistrés', label: 'Accidents enregistrés' },
      { key: 'Date dernier recensement', label: 'Date dernier recensement' },
      { key: 'Coordonnées GPS', label: 'Coordonnées GPS' }
    ],
    customHeader: (properties: any) => properties.Nom ? `Mejhar ${properties.Nom}` : 'Mejhar'
  },
  'zone-commerciale-chami': {
    title: 'Zone Commerciale Chami',
    fields: [
      { key: 'nom', label: 'Nom' },
      { key: 'type', label: 'Type' },
      { key: 'superficie', label: 'Superficie' }
    ]
  },
  'zone-commerciale-sfariyatt': {
    title: 'Zone Commerciale Sfariyatt',
    fields: [
      { key: 'nom', label: 'Nom' },
      { key: 'type', label: 'Type' },
      { key: 'superficie', label: 'Superficie' }
    ]
  },
  // Add more layer configurations as needed
  default: {
    title: 'Feature',
    fields: [
      { key: 'type', label: 'Type' },
      { key: 'description', label: 'Description' },
      { key: 'wilaya', label: 'Wilaya' }
    ]
  }
};

const FeaturePopup: React.FC<FeaturePopupProps> = ({ feature, position, onClose, onZoom }) => {
  console.log('FeaturePopup rendering with:', { feature, position });
  console.log('Feature properties:', feature.properties);
  console.log('Feature layerType:', feature.layerType);
  
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [currentPosition, setCurrentPosition] = useState(position);
  const popupRef = useRef<HTMLDivElement>(null);

  // Get configuration for this layer type
  const config = LAYER_CONFIGS[feature.layerType] || LAYER_CONFIGS.default;
  console.log('Selected popup config:', config);
  console.log('Available configs:', LAYER_CONFIGS);

  // Handle dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    if (popupRef.current) {
      const rect = popupRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      setCurrentPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Set up global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Ensure popup stays within viewport
  const adjustedPosition = {
    x: Math.max(10, Math.min(currentPosition.x, window.innerWidth - 320)),
    y: Math.max(10, Math.min(currentPosition.y, window.innerHeight - 200))
  };

  return (
    <Paper
      ref={popupRef}
      elevation={8}
      sx={{
        minWidth: 280,
        maxWidth: 370,
        borderRadius: 2.5,
        background: 'rgba(255,255,255,0.92)',
        boxShadow: '0 2px 12px rgba(21,105,239,0.08)',
        border: '1px solid #eee',
        p: 0,
        overflow: 'hidden',
        backdropFilter: 'blur(2.5px)',
        userSelect: 'none',
        position: 'fixed',
        left: adjustedPosition.x,
        top: adjustedPosition.y,
        fontSize: 11,
        transition: 'box-shadow 0.18s, border 0.18s',
        '@media (max-width: 600px)': {
          minWidth: 0,
          maxWidth: '98vw',
          borderRadius: 8,
          p: 0.25
        }
      }}
    >
      <Box
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          position: 'relative', 
          height: 18, 
          mb: 0, 
          cursor: 'move', 
          userSelect: 'none',
          backgroundColor: feature.layerType === 'petite-mine' ? 'rgba(0, 0, 0, 0.9)' : 'transparent',
          borderRadius: feature.layerType === 'petite-mine' ? '8px 8px 0 0' : '0'
        }}
        onMouseDown={e => {
          // Only start drag if not clicking the close button
          if (!(e.target as HTMLElement).closest('.popup-close-btn')) {
            if (e.button === 0) handleMouseDown(e);
          }
        }}
      >
        {/* Title centered */}
        <Typography
          sx={{
            fontFamily: 'Louguiya, sans-serif',
            fontSize: '0.78rem',
            fontWeight: 700,
            color: feature.layerType === 'petite-mine' ? '#ffffff' : '#111',
            textAlign: 'center',
            flex: 1,
            p: 0,
            lineHeight: '20px',
            height: 20,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {config.customHeader 
            ? config.customHeader(feature.properties)
            : (feature.name && feature.name.startsWith(config.title)
              ? feature.name
              : `${config.title} ${feature.name}`)}
        </Typography>
        {/* Close button right */}
        <Box sx={{ position: 'absolute', top: '50%', right: 6, transform: 'translateY(-50%)', zIndex: 3, display: 'flex', gap: 1 }}>
          <IconButton
            className="popup-close-btn"
            onClick={onClose}
            size="small"
            sx={{
              color: '#1976d2',
              background: 'rgba(230,245,255,0.7)',
              border: '1px solid #dbeafe',
              boxShadow: '0 2px 8px rgba(21,105,239,0.08)',
              width: 16,
              height: 16,
              p: 0,
              '&:hover': { backgroundColor: 'rgba(21,105,239,0.09)' }
            }}
          >
            <CloseIcon sx={{ fontSize: 11 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 0.7 }}>
        {/* Separator line */}
        <Box
          sx={{
            width: '100%',
            height: '1px',
            backgroundColor: 'rgba(0, 0, 0, 0.10)',
            mb: 0.08
          }}
        />

        {/* Feature properties */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
          {/* First, try to show properties using the config */}
          {config.fields.map((field) => {
            const value = feature.properties[field.key];
            return (
              <Box key={field.key} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.1 }}>
                <Typography className="label" sx={{ minWidth: 55, fontWeight: 600, color: '#111', fontSize: 10 }}>{field.label}</Typography>
                <Typography className="value" sx={{ fontWeight: 400, color: '#111', fontSize: 10, ml: 0.7 }}>
                  {value !== undefined && value !== null && value !== ''
                    ? (field.formatter ? field.formatter(value) : value)
                    : <span style={{ color: '#bdbdbd', fontStyle: 'italic' }}>Donnée manquante</span>}
                </Typography>
              </Box>
            );
          })}
          
          {/* If no properties matched the config fields, show all available properties as fallback */}
          {config.fields.every(field => feature.properties[field.key] === undefined) && 
            Object.entries(feature.properties || {}).map(([key, value]) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.2 }}>
                <Typography className="label" sx={{ minWidth: 60, fontWeight: 600, color: '#111', fontSize: 10 }}>{key}</Typography>
                <Typography className="value" sx={{ fontWeight: 400, color: '#111', fontSize: 10, ml: 0.7 }}>
                  {value !== undefined && value !== null && value !== ''
                    ? value
                    : <span style={{ color: '#bdbdbd', fontStyle: 'italic' }}>Donnée manquante</span>}
                </Typography>
              </Box>
            ))
          }
        </Box>
      </Box>
      {/* Zoom icon bottom right */}
      <Box sx={{ position: 'absolute', bottom: 5, right: 7, zIndex: 3 }}>
        <IconButton
          size="small"
          sx={{ color: '#111', background: 'rgba(255,255,255,0.10)', border: '1px solid #eee', boxShadow: '0 1px 4px rgba(136,136,136,0.04)', p: 0.5 }}
          onClick={onZoom}
          title="Zoom to feature"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><polyline points="8 11 11 8 14 11" /><line x1="11" y1="8" x2="11" y2="14" /></svg>
        </IconButton>
      </Box>
    </Paper>
  );
};

export default FeaturePopup;
